/**
 * useMaintenanceData / useActiveMaintenance Hooks
 * 점검 관리 데이터 훅
 *
 * @description
 * - 점검 목록 조회/등록/수정/삭제 (CRUD)
 * - 점검 문구 코드 목록 조회
 * - 활성 점검 상태 실시간 확인 (1분마다 폴링)
 * - useRef를 사용한 중복 API 호출 방지
 */
import {useCallback, useEffect, useRef, useState} from "react";
import { maintenanceApi } from '../api/maintenanceApi';
import {
    CreateMaintenanceRequestDto,
    GetActiveMaintenanceResponseDto,
    Maintenance,
    maintenanceListFromDto,
    UpdateMaintenanceRequestDto,
} from '../types/maintenance.types';
import { commonCodeApi } from "@/shared/api/commonCodeApi";
import { CommonCodeDto } from "@/shared/types/commonCode.types";
import { ToastType } from "@/shared/components/feedback/Toast/Toast.types";
import { useToast } from "@/shared/components/feedback/Toast/ToastContext";

/**
 * 점검 관리 데이터 훅
 * @returns {Object} 점검 관련 상태 및 함수
 * @property {Maintenance[]} maintenances - 점검 목록
 * @property {boolean} loading - 로딩 상태
 * @property {CommonCodeDto[]} msgCodes - 점검 문구 코드 목록
 * @property {Function} fetchMaintenances - 점검 목록 조회 함수
 * @property {Function} createMaintenance - 점검 등록 함수
 * @property {Function} updateMaintenance - 점검 수정 함수
 * @property {Function} deleteMaintenance - 점검 삭제 함수
 */
export const useMaintenanceData = () => {
    const { showToast } = useToast();
    const [maintenances, setMaintenances] = useState<Maintenance[]>([]);
    const [loading, setLoading] = useState(false);
    const [msgCodes, setMsgCodes] = useState<CommonCodeDto[]>([]);
    const hasFetchedMsgCodes = useRef(false);

    /**
     * 점검 목록 조회
     */
    const fetchMaintenances = useCallback(async () => {
        try {
            setLoading(true);
            const response = await maintenanceApi.getMaintenanceList();
            const list = maintenanceListFromDto(response);
            setMaintenances(list);
        } catch {
            showToast('점검 목록을 불러오는데 실패했습니다.', { type: ToastType.ERROR });
            setMaintenances([]);
        } finally {
            setLoading(false);
        }
    }, [showToast]);

    /**
     * 점검 등록
     */
    const createMaintenance = useCallback(async (request: CreateMaintenanceRequestDto): Promise<boolean> => {
        try {
            setLoading(true);
            const response = await maintenanceApi.createMaintenance(request);
            if (response.success) {
                showToast('점검이 등록되었습니다.', { type: ToastType.SUCCESS });
                await fetchMaintenances();
                return true;
            } else {
                showToast(response.message || '점검 등록에 실패했습니다.', { type: ToastType.ERROR });
                return false;
            }
        } catch {
            showToast('점검 등록에 실패했습니다.', { type: ToastType.ERROR });
            return false;
        } finally {
            setLoading(false);
        }
    }, [showToast, fetchMaintenances]);

    /**
     * 점검 수정
     */
    const updateMaintenance = useCallback(async (request: UpdateMaintenanceRequestDto): Promise<boolean> => {
        try {
            setLoading(true);
            const response = await maintenanceApi.updateMaintenance(request);
            if (response.success) {
                showToast('점검이 수정되었습니다.', { type: ToastType.SUCCESS });
                await fetchMaintenances();
                return true;
            } else {
                showToast(response.message || '점검 수정에 실패했습니다.', { type: ToastType.ERROR });
                return false;
            }
        } catch {
            showToast('점검 수정에 실패했습니다.', { type: ToastType.ERROR });
            return false;
        } finally {
            setLoading(false);
        }
    }, [showToast, fetchMaintenances]);

    /**
     * 점검 삭제
     */
    const deleteMaintenance = useCallback(async (seqNo: number): Promise<boolean> => {
        try {
            setLoading(true);
            const response = await maintenanceApi.deleteMaintenance(seqNo);
            if (response.success) {
                showToast('점검이 삭제되었습니다.', { type: ToastType.SUCCESS });
                await fetchMaintenances();
                return true;
            } else {
                showToast(response.message || '점검 삭제에 실패했습니다.', { type: ToastType.ERROR });
                return false;
            }
        } catch {
            showToast('점검 삭제에 실패했습니다.', { type: ToastType.ERROR });
            return false;
        } finally {
            setLoading(false);
        }
    }, [showToast, fetchMaintenances]);

    // 점검 문구 코드 목록 조회
    useEffect(() => {
        if (hasFetchedMsgCodes.current) return;

        let isCancelled = false;

        const fetchMsgCodes = async () => {
            try {
                const response = await commonCodeApi.getCommonCodeList('MAINT_MSG');
                if (!isCancelled) {
                    setMsgCodes(response.codes || []);
                    hasFetchedMsgCodes.current = true;
                }
            } catch {
                // 에러는 무시 (취소된 경우 포함)
            }
        };

        fetchMsgCodes();
        return () => { isCancelled = true; };
    }, []);

    // 초기 데이터 로드
    useEffect(() => {
        fetchMaintenances();
    }, [fetchMaintenances]);

    return {
        maintenances,
        loading,
        msgCodes,
        fetchMaintenances,
        createMaintenance,
        updateMaintenance,
        deleteMaintenance
    };
};

/**
 * 현재 활성화된 점검 조회 훅 (협력사용)
 * @description 1분마다 활성 점검 상태를 폴링하여 확인
 * @returns {Object} 활성 점검 관련 상태 및 함수
 * @property {GetActiveMaintenanceResponseDto | null} activeMaintenance - 활성 점검 정보
 * @property {boolean} isMaintenance - 점검 중 여부
 * @property {boolean} loading - 로딩 상태
 * @property {Function} refetch - 활성 점검 재조회 함수
 */
export const useActiveMaintenance = () => {
    const [activeMaintenance, setActiveMaintenance] = useState<GetActiveMaintenanceResponseDto | null>(null);
    const [loading, setLoading] = useState(true);

    const fetchActiveMaintenance = useCallback(async () => {
        try {
            setLoading(true);
            const response = await maintenanceApi.getActiveMaintenance();
            setActiveMaintenance(response);
        } catch {
            setActiveMaintenance(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchActiveMaintenance();

        // 1분마다 점검 상태 확인
        const interval = setInterval(fetchActiveMaintenance, 60000);
        return () => clearInterval(interval);
    }, [fetchActiveMaintenance]);

    return {
        activeMaintenance,
        isMaintenance: activeMaintenance?.isMaintenance ?? false,
        loading,
        refetch: fetchActiveMaintenance
    };
};
